import { useState } from 'react'
import { ArrowRight, Loader2, Check } from 'lucide-react'
import { supabase } from '@/lib/supabaseClient'
import WaterRipple from '@/components/sections/WaterRipple'

export default function Newsletter() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setStatus('loading')
    setErrorMessage(null)
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/complete-account`,
          data: {
            marketing_consent: true,
          },
        },
      })
      if (error) throw error
      setStatus('success')
      setEmail('')
    } catch (err) {
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  return (
    <section className="relative py-24 md:py-32 bg-primary overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-5 mix-blend-overlay pointer-events-none" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <WaterRipple />

      <div className="container relative z-10">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-6">
            Stay in the Hunt
          </h2>
          <h3 className="text-3xl md:text-5xl font-bold font-heading text-white mb-6 tracking-tight uppercase leading-[0.95]">
            Get the <span className="text-accent">Edge.</span>
          </h3>
          <p className="text-slate-400 text-lg mb-10 font-light leading-relaxed">
            Insights, strategies, and the occasional alpha. Delivered to your inbox. No fluff.
          </p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex items-center rounded-full border border-white/10 bg-white/5 overflow-hidden hover:border-white/20 focus-within:border-accent/60 transition-all">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                disabled={status === 'loading'}
                className="flex-1 min-w-0 px-6 py-4 bg-transparent border-0 text-white placeholder:text-slate-500 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-12 h-12 rounded-full bg-accent hover:bg-accent/90 text-white shrink-0 transition-colors disabled:opacity-50 flex items-center justify-center my-1 mr-1"
              >
                {status === 'loading' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : status === 'success' ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <ArrowRight className="h-5 w-5" />
                )}
              </button>
            </div>
          </form>

          {status === 'success' && (
            <p className="mt-4 text-accent text-sm font-medium">
              Check your inbox for a verification link to complete your account.
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 text-red-400 text-sm">
              {errorMessage || 'Something went wrong.'} Try again or{' '}
              <a href="mailto:hello@gigafish.co" className="underline hover:text-accent">
                reach out directly
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
